import React from 'react';
import { ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Visual indicator shown next to a candidate's name when their CV was
 * parsed via OCR. Warns the recruiter that some values may have minor
 * OCR artefacts and should be double-checked.
 *
 * Props:
 *   - active (bool): render only when true; returns null otherwise.
 *   - size ('sm' | 'md'): controls icon size and padding (default 'md').
 *   - withLabel (bool): show the "CV scanné" text (default true).
 */
export default function OcrBadge({ active, size = 'md', withLabel = true }) {
    const { t } = useTranslation();
    if (!active) return null;

    const sizing = size === 'sm'
        ? 'text-[10px] px-1.5 py-0.5 gap-1'
        : 'text-xs px-2 py-0.5 gap-1.5';
    const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        className={`inline-flex items-center rounded-full font-medium border border-[#F59E0B]/40 bg-[#FEF3C7] text-[#92400E] ${sizing}`}
                        data-testid="ocr-badge"
                        aria-label={t('candidates.ocr_badge_label')}
                    >
                        <ScanLine className={`${iconSize} flex-shrink-0`} />
                        {withLabel && <span>{t('candidates.ocr_badge_label')}</span>}
                    </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs" data-testid="ocr-badge-tooltip">
                    {t('candidates.ocr_badge_tooltip')}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
